/**
 * NMU-592: moving a sub-item up or down a Grid Content page.
 *
 * This lives outside the dialog because the dialog is a 2,900-line component
 * that cannot practically be rendered in a test, and the part worth testing is
 * not the buttons — it is what happens to the data underneath them. The
 * requirement the ticket spells out is that every field of an item travels with
 * it: "title, description, content body, uploaded files ... without losing
 * entered form data". A swap that copies the visible fields and forgets
 * `attachImage` leaves an item's picture behind on the item it changed places
 * with, and that is invisible until someone saves and looks at the page.
 *
 * So the whole item object moves, by reference, and these functions are pure —
 * they build a new array and never mutate the one they are given, which is what
 * lets React see the change.
 */

/**
 * @param {Array} list  the sub-items, in display order
 * @param {number} index  the item to move
 * @param {number} offset  -1 to move it up, +1 to move it down
 * @returns {Array} a new array; the original when the move is not possible
 */
export function moveItem(list, index, offset) {
  if (!Array.isArray(list)) return list;

  const target = index + offset;
  // Off either end: the caller disables the button, but a keyboard, a double
  // click on the last frame before a re-render, or a test can still ask.
  if (index < 0 || index >= list.length) return list;
  if (target < 0 || target >= list.length) return list;

  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * Stamp each sub-item with its position, counted from 0.
 *
 * The order already survives without this — the array is sent in order and
 * Mongo keeps a list as an array — so this states the order rather than
 * creating it. The value always comes from the array itself, never from
 * whatever `displayOrder` an item was carrying, so a stale value read from the
 * server cannot outvote the order the author just arranged.
 */
export function withDisplayOrder(list) {
  if (!Array.isArray(list)) return list;
  return list.map((item, index) => ({ ...item, displayOrder: index }));
}
